/**
 * Renders the real Wishlist page from the device-local `bh:wishlist`
 * localStorage list (see assets/wishlist.js / product-wishlist-button.js —
 * same THEME_NATIVE, device-local-only contract, no account sync). Since
 * there's no server-side list of "this device's saved products," each
 * saved product handle is fetched from Shopify's real `/products/{handle}.js`
 * endpoint client-side — real product data (title/price/image/availability),
 * never fabricated.
 */
const STORAGE_KEY = 'bh:wishlist';

function getWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setWishlist(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage unavailable — nothing to persist.
  }
}

function formatMoney(cents) {
  return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });
}

class WishlistGrid extends HTMLElement {
  async connectedCallback() {
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.countText = this.querySelector('[data-wishlist-count-text]');
    this.addAllButton = this.querySelector('[data-wishlist-add-all]');
    this.toast = this.querySelector('[data-wishlist-toast]');

    this.addAllButton?.addEventListener('click', () => this.addAllToCart());

    await this.render();
    document.addEventListener('wishlist:updated', () => this.render());
  }

  async render() {
    const handles = getWishlist();

    if (this.countText) {
      this.countText.textContent =
        handles.length === 1
          ? this.countText.dataset.oneLabel || '1 saved set · saved on this device'
          : `${handles.length} ${this.countText.dataset.otherLabel || 'saved sets · saved on this device'}`;
    }

    if (handles.length === 0) {
      this.loading.hidden = true;
      this.grid.hidden = true;
      this.addAllButton.hidden = true;
      this.empty.hidden = false;
      return;
    }

    this.empty.hidden = true;
    this.loading.hidden = false;
    this.grid.hidden = true;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return null;
          return await res.json();
        } catch {
          return null;
        }
      })
    );

    const validProducts = products.filter(Boolean);

    // Drop any handles that no longer resolve to a real product (deleted/unpublished).
    if (validProducts.length !== handles.length) {
      setWishlist(validProducts.map((p) => p.handle));
    }

    this.loading.hidden = true;

    if (validProducts.length === 0) {
      this.empty.hidden = false;
      this.addAllButton.hidden = true;
      return;
    }

    this.grid.hidden = false;
    this.addAllButton.hidden = false;
    this.grid.innerHTML = validProducts.map((p) => this.cardHTML(p)).join('');

    this.grid.querySelectorAll('[data-remove-handle]').forEach((button) => {
      button.addEventListener('click', () => this.removeItem(button.dataset.removeHandle));
    });

    this.grid.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.addEventListener('click', () => this.addToCart(button, button.dataset.variantId));
    });
  }

  cardHTML(product) {
    const variant = product.variants[0];
    const available = variant?.available;
    const compareAt = variant?.compare_at_price;
    const showCompare = compareAt && compareAt > variant.price;

    return `
      <div class="wishlist-card">
        <div class="wishlist-card__media">
          ${
            product.featured_image
              ? `<a href="${product.url}"><img src="${product.featured_image}" alt="${escapeHtml(product.title)}" loading="lazy"></a>`
              : ''
          }
          <button type="button" class="wishlist-card__remove" data-remove-handle="${product.handle}" aria-label="${window.wishlistPageStrings?.remove || 'Remove'} ${escapeHtml(product.title)}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><path d="M12 20s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 5.5 5.5 5.5 0 0 1 21.5 11c-2.5 4.65-9.5 9-9.5 9z"></path></svg>
          </button>
        </div>
        <div class="wishlist-card__content">
          <h3 class="wishlist-card__title"><a href="${product.url}">${escapeHtml(product.title)}</a></h3>
          <div class="wishlist-card__price">
            <span class="wishlist-card__price-current">${formatMoney(variant.price)}</span>
            ${showCompare ? `<span class="wishlist-card__price-compare">${formatMoney(compareAt)}</span>` : ''}
          </div>
          <div class="wishlist-card__availability wishlist-card__availability--${available ? 'in-stock' : 'sold-out'}">
            ${available ? window.wishlistPageStrings?.inStock || 'In Stock' : window.wishlistPageStrings?.soldOut || 'Sold Out'}
          </div>
          <button type="button" class="wishlist-card__cta" data-add-to-cart data-variant-id="${variant.id}" ${available ? '' : 'disabled'}>
            ${available ? window.wishlistPageStrings?.addToCart || 'Add to Cart' : window.wishlistPageStrings?.soldOut || 'Sold Out'}
          </button>
        </div>
      </div>
    `;
  }

  removeItem(handle) {
    const wishlist = getWishlist().filter((h) => h !== handle);
    setWishlist(wishlist);
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  async addToCart(button, variantId) {
    button.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.showToast(window.wishlistPageStrings?.addedOne || 'Added to cart');
    } finally {
      button.disabled = false;
    }
  }

  async addAllToCart() {
    const handles = getWishlist();
    const items = Array.from(this.grid.querySelectorAll('[data-add-to-cart]:not(:disabled)')).map((button) => ({
      id: Number(button.dataset.variantId),
      quantity: 1,
    }));

    if (items.length === 0) return;

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    this.showToast(
      (window.wishlistPageStrings?.addedMany || 'Added {count} item(s) to cart').replace('{count}', items.length)
    );
  }

  showToast(message) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.hidden = false;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toast.hidden = true;
    }, 2400);
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
